import {
    useCallback,
    useEffect, useMemo, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { getClientId, isQRIban } from 'utils';
import { portfolioSelector, usePortfolioSelector } from 'domain/Portfolio';
import { useProductsList } from 'hooks/useProducts';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import { useWithdrawalPlanBase } from 'widgets/WithdrawalPlan/hooks/useWithdrawalPlanBase';
import { getModel } from 'widgets/WithdrawalPlan/models/getModel';
import { ErrorData } from 'widgets/WithdrawalPlan/types';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { STATUS_OK } from 'constants/constants';
import { useCachedAPI } from 'hooks/useCachedAPI';
import { useTranslation } from 'react-i18next';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import HandlerError from 'errors/HandlerError';
import { PortfolioWithdrawalDataResponse } from 'core/types/api/Api';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { validateIban } from 'utils/validation';
import { useLocalData } from 'hooks/helpers/useLocalData';
import { roundToNearestTenth } from 'utils/formatting';
import { useChangeWithdrawal } from './ChangeWithdrawalManager';
import { ACTIVE_SWITCHER_STOP } from './constants';

const useWithdrawalChangePlan = () => {
    const navigate = useNavigate();
    const { i18n: { language } } = useTranslation();

    const { pathname } = useLocation();
    const currentStepKey:string = pathname.split('/').pop() as string;

    const { dfsClientId, portfolioId } = useParams();
    const contactId = getClientId(dfsClientId);
    const [pageErrors, setPageErrors] = useState<ErrorData | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [withdrawalData, setWithdrawalData, clear] = useLocalData<PortfolioWithdrawalDataResponse | null>({
        storageType: 'sessionStorage',
        key: 'advisory--withdrawalData',
        initialValue: {},
    });
    const [withdrawalDataFromAPI, setWithdrawalDataFromAPI] = useState<any>(null);

    const {
        getWithdrawalData,
    } = useCachedAPI();

    useEffect(() => {
        (async () => {
            try {
                const withDrawalDataFromAPI = await getWithdrawalData([{ portfolioId, language }]);

                setWithdrawalData((prev) => ({ ...prev, bankIban: withDrawalDataFromAPI?.bankIban || '' }));
                setWithdrawalDataFromAPI(withDrawalDataFromAPI);
            } catch (err) {
                handlerRequestCanceling(
                    HandlerError({
                        setError: setPageErrors,
                        setLoading: () => {},
                    }),
                )(err);
            }
        })();
    }, [portfolioId, language]);

    const { products } = useProductsList(contactId);

    const { dataRaw: portfolio } = usePortfolioSelector(portfolioSelector);

    const selectedProduct = products?.find(
        ({ id }) => portfolio?.Product?.Id === id,
    );

    const productSettings = useMemo(
        () => selectedProduct?.settings,
        [selectedProduct],
    );

    const {
        isAzp, isPk, isZiv, isZifd, isAzpD,
    } = useThreeBProductType(
        { product: { externalId: portfolio?.Product?.ExternalId } },
    );

    const {
        data,
        setData,
        errorData,
        trigger,
        setErrorData,
        clearErrors,
        setLoading,
        isLoading,
        optionWithdrawalPeriodicity,
        control,
        handleSubmit,
    } = useWithdrawalPlanBase({
        isAzp,
        isPk,
        isZiv,
        isZifd,
        isAzpD,
        productSettings,
        modelPortfolioId: portfolio?.ModelPortfolioId,
        setPageErrors,
        isChangeWithdrawal: currentStepKey === 'withdrawal-plan',
    });

    useEffect(() => {
        const firstName = withdrawalData?.accountHolder?.split(' ')[0];
        const lastName = withdrawalData?.accountHolder?.split(' ')[1];

        const adaptedData = getModel(
            {
                withdrawal: {
                    withdrawalAmount: withdrawalData?.withdrawalAmount,
                    periodicity: withdrawalData?.frequency,
                    startDate: withdrawalData?.startOfWithdrawal || null,
                },
                paymentInstruction: {
                    iban: '',
                    esr: withdrawalData?.esr,
                    firstName,
                    lastName,
                    bankName: withdrawalData?.bankName,
                    zipCity: withdrawalData?.zipCode,
                    city: withdrawalData?.city,
                },
            },
            { firstName, lastName, isAzp },
        );

        setData(adaptedData);
    }, [getModel, withdrawalData, isAzp]);

    const withdrawalPeriodicityOptions = useMemo(() => optionWithdrawalPeriodicity?.map((optn) => ({ value: optn?.id, label: optn?.label })), [optionWithdrawalPeriodicity]);
    const { activeSwitcher, actions: { handleAdjustWithdrawal, handleStopWithdrawal } } = useChangeWithdrawal();

    const { mutateAsync: onChangeWithdrawalPlan, isPending } = useGenericAPIMutation();

    const changeWithdrawalPlan = async ({ withdrawalPlanData }) => {
        const response:any = await onChangeWithdrawalPlan({
            service: 'customInvestmentService',
            API: 'changeWidthdrawalPlan',
            payload: {
                portfolioId,
                ...withdrawalPlanData,
            },
        });

        if (response?.status === STATUS_OK) {
            clear();
            navigate(`/client/${dfsClientId}/container/${response?.data?.investmentDescription?.containerId}`);
        }
    };

    const stopWithdrawalPlan = async () => {
        const response:any = await onChangeWithdrawalPlan({
            service: 'customInvestmentService',
            API: 'stopWithdrawalPlan',
            payload: {
                portfolioId,
            },
        });

        if (response?.status === STATUS_OK) {
            navigate(`/client/${dfsClientId}/container/${response?.data?.investmentDescription?.containerId}`);
        }
    };

    const onSubmit = handleSubmit && handleSubmit(async (formData) => {
        if (currentStepKey === 'withdrawal-plan' && formData?.amount && formData?.frequency) {
            setWithdrawalData(prev => ({
                ...prev,
                withdrawalAmount: formData?.amount,
                frequency: +(formData?.frequency as number),
                startDate: (formData?.startDate && formData?.startDate !== 'Invalid date') ? moment(formData?.startDate, 'MM/YYYY').format(moment.defaultFormatUtc) : null,
                startOfWithdrawal: (formData?.startDate && formData?.startDate !== 'Invalid date') ? moment(formData?.startDate, 'MM/YYYY').format(moment.defaultFormatUtc) : null,
                investmentAmount: formData?.amount,
            }));
            setStepComplete(true);
        } else {
            try {
                setSubmitting(true);
                const ibanError = await validateIban(formData?.iban);

                if (ibanError) {
                    setErrorData('iban', {
                        message: ibanError as string,
                    });

                    return;
                }

                const paymentInstruction:any = {
                    iban: formData?.iban,
                    firstName: formData?.firstName,
                    lastName: formData?.lastName,
                    bankName: formData?.bankName,
                    zipCity: formData?.zipCode,
                    city: formData?.city,
                };

                if (formData?.esr && isQRIban(formData?.iban)) {
                    paymentInstruction.esr = formData.esr;
                }

                const withdrawalDatas = {
                    withdrawal: {
                        withdrawalAmount: roundToNearestTenth(formData?.amount),
                        periodicity: +(formData?.frequency as number),
                        startDate: formData?.startDate ? moment(formData?.startDate, 'MM/YYYY').format(moment.defaultFormatUtc) : withdrawalDataFromAPI?.startOfWithdrawal,
                        investmentAmount: roundToNearestTenth(formData?.amount),
                    },
                    paymentInstruction,
                };

                await changeWithdrawalPlan({ withdrawalPlanData: withdrawalDatas });
            } catch (err) {
                handlerRequestCanceling(
                    HandlerError({
                        setError: setPageErrors,
                        setLoading,
                    }),
                )(err);
            } finally {
                setSubmitting(false);
            }
        }
    });

    const handleContinue = useCallback(async () => {
        if (activeSwitcher === ACTIVE_SWITCHER_STOP) {
            setSubmitting(true);

            try {
                await stopWithdrawalPlan();
            } catch (err) {
                handlerRequestCanceling(
                    HandlerError({
                        setError: setPageErrors,
                        setLoading,
                    }),
                )(err);
            } finally {
                setSubmitting(false);
            }
        } else if (onSubmit) {
            await onSubmit();
        }
    }, [onSubmit, activeSwitcher]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: handleContinue,
        isContinueDisabled: submitting,
        isContinueLoading: submitting,
        reSubscriptionStates: [activeSwitcher, withdrawalDataFromAPI?.startOfWithdrawal],
    });

    return {
        data,
        setData,
        control,
        trigger,
        errorData,
        setErrorData,
        pageErrors,
        setPageErrors,
        isLoading,
        setLoading,
        withdrawalDataFromAPI,
        withdrawalPeriodicityOptions,
        startDate: null,
        minStartDate: null,
        hasWithdrawalData: withdrawalData?.bankIban,
        inProgress: isPending || submitting,
        activeSwitcher,
        handleAdjustWithdrawal: () => {
            clearErrors();
            handleAdjustWithdrawal();
        },
        handleStopWithdrawal: () => {
            clearErrors();
            handleStopWithdrawal();
        },
        handleSubmit,
        changeWithdrawalPlan,
        stopWithdrawalPlan,
        portfolioName: portfolio?.Name || '',
    };
};

export default useWithdrawalChangePlan;
